import { useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import LoadingScreen from '@/components/shared/LoadingScreen'

interface AuthGuardProps {
  children: React.ReactNode
}

/**
 * Protects routes by requiring authentication.
 * Automatically redirects to Keycloak login if not authenticated.
 */
export default function AuthGuard({ children }: AuthGuardProps): React.JSX.Element | null {
  const { isAuthenticated, isLoading, platformBlocked, login, logout } = useAuth()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login()
    }
  }, [isLoading, isAuthenticated, login])

  // A silent token renewal also sets isLoading, and AuthContext renews on
  // every return to the tab. Unmounting here would cost a signed-in page its
  // filters, ticks and open forms, so only a tab with no session waits.
  if (isLoading && !isAuthenticated) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <LoadingScreen label="Redirecting to login..." />
  }

  if (platformBlocked) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-xl font-semibold">Platform access restricted</h1>
        <p>Your account does not have platform access. Contact your administrator.</p>
        <button className="underline" onClick={logout}>Sign out</button>
      </main>
    )
  }

  return <>{children}</>
}
